import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import ApiRequest from "../../auth/apiRequestHelper";
import {
  clearLocalLayout,
  isEmptyLayout,
  isLayout,
  layoutFor,
  moveId,
  orderProducts,
  readLocalLayout,
  writeLocalLayout,
} from "../../utils/productLayout";

// Per-shop product tile order for the sale screen.
//
//   layout  : { "<category_id>": [product_id, ...] } — the persisted document
//   source  : where it came from / where saves go. "api" once the backend
//             answered, "local" while the route is missing (404) or unreachable.
//   status  : "idle" | "loading" | "saving"
//   editing : null | { categoryId, draft: [product_id, ...] } — the drag-and-drop
//             draft of the category being rearranged (never persisted).

const initialState = {
  layout: {},
  source: "unknown",
  status: "idle",
  editing: null,
};

const isNotFound = (err) => err?.response?.status === 404;

const documentFrom = (response, fallback) => {
  const doc = response?.data?.product_layout;
  return isLayout(doc) ? doc : fallback;
};

export const loadProductLayout = createAsyncThunk(
  "layout/load",
  async (shopId) => {
    const local = readLocalLayout(shopId);
    try {
      const response = await ApiRequest.getProductLayout();
      let layout = documentFrom(response, {});
      if (isEmptyLayout(layout) && !isEmptyLayout(local)) {
        // First contact with a deployed backend: push what this device saved
        // while the route did not exist, once.
        const pushed = await ApiRequest.updateProductLayout(local);
        layout = documentFrom(pushed, local);
      }
      clearLocalLayout(shopId);
      return { layout, source: "api" };
    } catch (_) {
      return { layout: local, source: "local" };
    }
  }
);

export const saveCategoryLayout = createAsyncThunk(
  "layout/save",
  async ({ categoryId, productIds }, { getState, rejectWithValue }) => {
    const { layout, source } = getState().layout;
    const shopId = getState().cart?.userInfo?.shopId;
    const key = String(categoryId);

    const next = { ...layout };
    if (productIds.length === 0) delete next[key];
    else next[key] = productIds;

    if (source === "api") {
      try {
        const response = await ApiRequest.updateProductLayout({ [key]: productIds });
        return { layout: documentFrom(response, next), source: "api", persistedTo: "api" };
      } catch (err) {
        if (!isNotFound(err)) {
          return rejectWithValue({
            message: err?.response?.data?.error ?? err?.message ?? "save failed",
          });
        }
        // 404: the route is not deployed on this backend yet — keep it local.
      }
    }

    if (!writeLocalLayout(shopId, next)) {
      return rejectWithValue({ message: "local storage unavailable" });
    }
    return { layout: next, source: "local", persistedTo: "local" };
  }
);

const layoutSlice = createSlice({
  name: "layout",
  initialState,
  reducers: {
    startEditing: (state, action) => {
      state.editing = {
        categoryId: action.payload.categoryId,
        draft: [...action.payload.draft],
      };
    },
    stopEditing: (state) => {
      state.editing = null;
    },
    moveDraftItem: (state, action) => {
      if (!state.editing) return;
      const { activeId, overId } = action.payload;
      state.editing.draft = moveId(state.editing.draft, activeId, overId);
    },
    clearLayoutState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadProductLayout.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loadProductLayout.fulfilled, (state, action) => {
        state.layout = action.payload.layout;
        state.source = action.payload.source;
        state.status = "idle";
      })
      .addCase(loadProductLayout.rejected, (state) => {
        state.status = "idle";
      })
      .addCase(saveCategoryLayout.pending, (state) => {
        state.status = "saving";
      })
      .addCase(saveCategoryLayout.fulfilled, (state, action) => {
        state.layout = action.payload.layout;
        state.source = action.payload.source;
        state.status = "idle";
      })
      .addCase(saveCategoryLayout.rejected, (state) => {
        state.status = "idle";
      });
  },
});

export const { startEditing, stopEditing, moveDraftItem, clearLayoutState } =
  layoutSlice.actions;

export const selectEditing = (state) => state.layout?.editing ?? null;
export const selectLayoutStatus = (state) => state.layout?.status ?? "idle";

// Products of the selected subcategory in display order: the editing draft
// while that category is being rearranged, otherwise the stored layout.
export const selectDisplayedProducts = createSelector(
  [
    (state) => state.cart.products,
    (state) => state.cart.selectedSubcategoryId,
    (state) => state.layout?.layout,
    (state) => state.layout?.editing,
  ],
  (products, categoryId, layout, editing) => {
    const ids =
      editing && String(editing.categoryId) === String(categoryId)
        ? editing.draft
        : layoutFor(layout, categoryId);
    return orderProducts(products, ids);
  }
);

export default layoutSlice.reducer;
